import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "../lib/axios.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import {
  resolveAuthorizedPostAuthRedirectTarget,
  resolveDefaultPostLoginPath,
  sanitizeRedirectTarget
} from "../components/routing/loginRedirect.js";
import { clearClientAuthState, clearClientIdentityState } from "../utils/authCleanup.js";
import { fetchUserLeagues } from "./leaguesApi.js";

export const authApi = {
  // Login
  login: async (credentials) => {
    const response = await axiosInstance.post("/auth/login", credentials);
    return response.data;
  },

  // Logout
  logout: async () => {
    try {
      // Call backend to clear the cookie
      await axiosInstance.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearClientAuthState();
      // Redirect to login page
      window.location.href = "/login";
    }
  },
};

export const useRegisterUser = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const redirectTo = sanitizeRedirectTarget(searchParams.get("redirect"));
  return useMutation({
    mutationKey: ["register-user"],
    mutationFn: async (userDetails) => {
      // Combine with pending team only if it exists (legacy flow)
      const pendingTeam = localStorage.getItem("pendingTeam");
      const registrationData = {
        ...userDetails,
        ...(pendingTeam ? { team: JSON.parse(pendingTeam) } : {})
      };

      try {
        const response = await axiosInstance.post(
          "/auth/register",
          registrationData
        );
        return response.data;
      } catch (error) {
        console.error("Registration API error:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
        });
        throw error;
      }
    },
    onSuccess: async (data) => {
      console.log("Registration successful, setting up authentication");

      // Clear any state that could have leaked from a previous account before
      // the new account becomes active.
      clearClientIdentityState();

      // Store only user data, not the token (handled by cookies)
      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
      }

      // IMPORTANT: Set the session in Supabase client so AuthContext can detect it
      if (data.token) {
        try {
          // Set the session using the token returned from registration
          await supabase.auth.setSession({
            access_token: data.token,
            refresh_token: data.refresh_token || null // Use the correct property name
          });
          console.log("Supabase session established successfully");
        } catch (error) {
          console.error("Failed to set Supabase session:", error);
          // Continue anyway, the HTTP-only cookies should still work
        }
      }

      let userLeagues = Array.isArray(data.user?.leagues) ? data.user.leagues : [];
      try {
        userLeagues = await queryClient.fetchQuery({
          queryKey: ["user-leagues"],
          queryFn: fetchUserLeagues,
          staleTime: 0,
        });
      } catch (error) {
        console.warn("Unable to fetch user leagues after registration, using fallback navigation.", error);
      }

      const authorizedRedirect = resolveAuthorizedPostAuthRedirectTarget(redirectTo, userLeagues, data.user);
      if (authorizedRedirect) {
        console.log("Navigating to authorized redirect target", authorizedRedirect);
        navigate(authorizedRedirect, { replace: true });
        return;
      }

      const defaultPath = resolveDefaultPostLoginPath(userLeagues, data.user);
      console.log("Navigating to default post-registration path", defaultPath);
      navigate(defaultPath, { replace: true });
    },
    onError: (err) => {
      console.error("Registration mutation error:", err);
      return {
        error:
          err.response?.data?.error || "Registration failed. Please try again.",
      };
    },
  });
};
